import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Avatar,
  IconButton,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  TextField,
  InputAdornment,
  Rating,
  Grid,
  Stack,
  Snackbar,
  Alert,
  alpha,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  VisibilityOff as HideIcon,
  FormatQuote as QuoteIcon,
  Close as CloseIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import {
  getTestimonials,
  deleteTestimonial,
  patchTestimonial,
} from "../../../Services/Services";
import TestimonialForm from "./TestimonialForm";

import { brandColor as brand } from "../../../theme";

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "4px",
    backgroundColor: "#f8fafc",
    transition: "all 0.2s ease",
    "& fieldset": { borderColor: "#e5e7eb" },
    "&:hover fieldset": { borderColor: "#c7d2fe" },
    "&.Mui-focused fieldset": { borderColor: brand, borderWidth: 1.5 },
  },
  "& .MuiInputLabel-root": { fontSize: "0.78rem", color: "#6b7280" },
  "& .MuiInputLabel-root.Mui-focused": { color: brand },
};

const TestimonialList = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    testimonial: null,
  });
  const [deleting, setDeleting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    setLoading(true);
    try {
      const data = await getTestimonials();
      setTestimonials(Array.isArray(data) ? data : []);
    } catch {
      setSnackbar({
        open: true,
        message: "Failed to load testimonials.",
        severity: "error",
      });
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    if (!deleteDialog.testimonial) return;
    setDeleting(true);
    try {
      const result = await deleteTestimonial(deleteDialog.testimonial.id);
      if (result) {
        setSnackbar({
          open: true,
          message: "Testimonial deleted successfully.",
          severity: "success",
        });
        fetchTestimonials();
      } else {
        setSnackbar({
          open: true,
          message: "Failed to delete testimonial.",
          severity: "error",
        });
      }
    } catch {
      setSnackbar({
        open: true,
        message: "Error deleting testimonial.",
        severity: "error",
      });
    }
    setDeleting(false);
    setDeleteDialog({ open: false, testimonial: null });
  };

  const handleToggleActive = async (testimonial) => {
    try {
      const result = await patchTestimonial(testimonial.id, {
        is_active: !testimonial.is_active,
      });
      if (result) {
        setSnackbar({
          open: true,
          message: `Testimonial ${testimonial.is_active ? "hidden" : "visible"} on site.`,
          severity: "success",
        });
        fetchTestimonials();
      }
    } catch {
      setSnackbar({
        open: true,
        message: "Failed to update status.",
        severity: "error",
      });
    }
  };

  const handleEdit = (id) => {
    setEditId(id);
    setShowForm(true);
  };

  const handleAdd = () => {
    setEditId(null);
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditId(null);
    fetchTestimonials();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditId(null);
  };

  const getInitials = (name = "") =>
    name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase();

  const filtered = testimonials.filter(
    (t) =>
      t.name?.toLowerCase().includes(search.toLowerCase()) ||
      t.role?.toLowerCase().includes(search.toLowerCase()),
  );

  if (showForm) {
    return (
      <TestimonialForm
        editId={editId}
        onSuccess={handleFormSuccess}
        onCancel={handleFormCancel}
      />
    );
  }

  return (
    <Box
      sx={{ px: 0, pt: 0, mt: -1, maxWidth: 1200, mx: "auto", width: "100%" }}
    >
      <Paper
        sx={{
          borderRadius: "4px",
          border: "1px solid #e5e7eb",
          boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
      >
        {/* ── Header ── */}
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            backgroundColor: "#f8fafc",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "4px",
                  bgcolor: alpha(brand, 0.1),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <QuoteIcon sx={{ color: brand, fontSize: 18 }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#111827",
                    fontSize: "1rem",
                    lineHeight: 1.2,
                    textAlign: "left",
                  }}
                >
                  Testimonials
                </Typography>
                <Typography
                  sx={{ fontSize: "0.7rem", color: "#6b7280", mt: 0.2 }}
                >
                  Manage client testimonials displayed on the landing page
                </Typography>
              </Box>
              <Chip
                label={`${filtered.length} Testimonial${filtered.length !== 1 ? "s" : ""}`}
                size="small"
                sx={{
                  borderRadius: "4px",
                  bgcolor: alpha(brand, 0.1),
                  color: brand,
                  fontSize: "0.65rem",
                  fontWeight: 600,
                  height: 20,
                }}
              />
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                placeholder="Search by name or role..."
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ ...textFieldSx, width: 220 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#9ca3af", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                  sx: { height: 34 },
                }}
              />
              <Button
                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                onClick={handleAdd}
                size="small"
                variant="contained"
                sx={{
                  borderRadius: "4px",
                  textTransform: "none",
                  fontSize: "0.75rem",
                  height: 34,
                  px: 2,
                  bgcolor: brand,
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#4338ca", boxShadow: "none" },
                }}
              >
                Add Testimonial
              </Button>
            </Stack>
          </Box>
        </Box>

        {/* ── Content ── */}
        <Box sx={{ p: 2.5 }}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 300,
              }}
            >
              <CircularProgress sx={{ color: brand }} />
            </Box>
          ) : filtered.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 8,
                border: "1.5px dashed #e5e7eb",
                borderRadius: "4px",
                bgcolor: "#f8fafc",
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "4px",
                  bgcolor: alpha(brand, 0.08),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 1.5,
                }}
              >
                <QuoteIcon sx={{ fontSize: 28, color: alpha(brand, 0.3) }} />
              </Box>
              <Typography
                sx={{ fontWeight: 600, color: "#374151", fontSize: "0.85rem" }}
              >
                No testimonials found
              </Typography>
              <Typography
                sx={{ fontSize: "0.72rem", color: "#9ca3af", mt: 0.5 }}
              >
                {search
                  ? "Try a different search term"
                  : "Add your first testimonial to get started"}
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {filtered.map((item) => {
                const isExpanded = expandedId === item.id;
                return (
                  <Grid item xs={12} sm={6} md={4} key={item.id}>
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: "4px",
                        border: "1px solid #e5e7eb",
                        overflow: "hidden",
                        position: "relative",
                        transition: "all 0.25s ease",
                        cursor: "default",
                        "&:hover": {
                          borderColor: "#c7d2fe",
                          boxShadow: "0 8px 24px rgba(79,70,229,0.1)",
                          transform: "translateY(-2px)",
                          "& .card-quote-icon": {
                            transform: "scale(1.1) rotate(-5deg)",
                            color: brand,
                          },
                          "& .card-overlay": {
                            opacity: 1,
                          },
                        },
                      }}
                    >
                      {/* Status bar */}
                      <Box
                        sx={{
                          height: 3,
                          background: item.is_active
                            ? "linear-gradient(90deg, #10b981, #34d399)"
                            : "linear-gradient(90deg, #ef4444, #f87171)",
                        }}
                      />

                      {/* Order badge */}
                      <Chip
                        label={`#${item.display_order}`}
                        size="small"
                        sx={{
                          position: "absolute",
                          top: 10,
                          right: 8,
                          borderRadius: "4px",
                          bgcolor: alpha(brand, 0.08),
                          color: brand,
                          fontSize: "0.58rem",
                          fontWeight: 700,
                          height: 18,
                          minWidth: 28,
                          zIndex: 2,
                        }}
                      />

                      {/* Quote section */}
                      <Box
                        sx={{
                          px: 2,
                          pt: 2,
                          pb: 1.5,
                          position: "relative",
                          minHeight: 100,
                        }}
                      >
                        {/* Large decorative quote */}
                        <QuoteIcon
                          className="card-quote-icon"
                          sx={{
                            position: "absolute",
                            top: 8,
                            left: 12,
                            fontSize: 32,
                            color: alpha(brand, 0.08),
                            transition: "all 0.3s ease",
                            zIndex: 0,
                          }}
                        />

                        {/* Review text */}
                        <Typography
                          onClick={() =>
                            setExpandedId(isExpanded ? null : item.id)
                          }
                          sx={{
                            fontSize: "0.78rem",
                            color: "#374151",
                            lineHeight: 1.6,
                            fontStyle: "italic",
                            position: "relative",
                            zIndex: 1,
                            pl: 1,
                            cursor: "pointer",
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            WebkitLineClamp: isExpanded ? "unset" : 3,
                            overflow: isExpanded ? "visible" : "hidden",
                            transition: "all 0.3s ease",
                          }}
                        >
                          "{item.text}"
                        </Typography>

                        {item.text?.length > 120 && (
                          <Typography
                            onClick={() =>
                              setExpandedId(isExpanded ? null : item.id)
                            }
                            sx={{
                              fontSize: "0.65rem",
                              color: brand,
                              fontWeight: 600,
                              cursor: "pointer",
                              mt: 0.5,
                              pl: 1,
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            {isExpanded ? "Show less" : "Read more"}
                          </Typography>
                        )}

                        {/* Rating */}
                        <Box sx={{ mt: 1.2, pl: 1 }}>
                          <Rating
                            value={item.rating || 0}
                            readOnly
                            size="small"
                            icon={
                              <StarIcon
                                sx={{ fontSize: 16, color: "#f59e0b" }}
                              />
                            }
                            emptyIcon={
                              <StarIcon
                                sx={{ fontSize: 16, color: "#e5e7eb" }}
                              />
                            }
                          />
                        </Box>
                      </Box>

                      {/* Divider */}
                      <Box
                        sx={{
                          mx: 2,
                          borderTop: "1px solid #f1f5f9",
                        }}
                      />

                      {/* Author section */}
                      <Box
                        sx={{
                          px: 2,
                          py: 1.5,
                          display: "flex",
                          alignItems: "center",
                          gap: 1.2,
                        }}
                      >
                        {/* Avatar */}
                        {item.logo ? (
                          <Avatar
                            src={item.logo}
                            sx={{
                              width: 40,
                              height: 40,
                              border: `2px solid ${alpha(brand, 0.15)}`,
                              boxShadow: `0 2px 8px ${alpha(brand, 0.1)}`,
                            }}
                          />
                        ) : (
                          <Avatar
                            sx={{
                              width: 40,
                              height: 40,
                              bgcolor: alpha(brand, 0.1),
                              color: brand,
                              fontSize: "0.78rem",
                              fontWeight: 800,
                              border: `2px solid ${alpha(brand, 0.15)}`,
                            }}
                          >
                            {item.initials || getInitials(item.name)}
                          </Avatar>
                        )}

                        {/* Name & role */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: "#111827",
                              fontSize: "0.8rem",
                              lineHeight: 1.2,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={item.name}
                          >
                            {item.name}
                          </Typography>
                          {item.role && (
                            <Typography
                              sx={{
                                fontSize: "0.68rem",
                                color: "#6b7280",
                                mt: 0.15,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              title={item.role}
                            >
                              {item.role}
                            </Typography>
                          )}
                        </Box>

                        {/* Status chip */}
                        <Chip
                          label={item.is_active ? "Active" : "Hidden"}
                          size="small"
                          sx={{
                            borderRadius: "4px",
                            bgcolor: item.is_active ? "#ecfdf5" : "#fef2f2",
                            color: item.is_active ? "#059669" : "#dc2626",
                            fontSize: "0.58rem",
                            fontWeight: 600,
                            height: 18,
                            flexShrink: 0,
                          }}
                        />
                      </Box>

                      {/* Actions footer */}
                      <Box
                        sx={{
                          px: 1.5,
                          py: 1,
                          bgcolor: "#f8fafc",
                          borderTop: "1px solid #f1f5f9",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#9ca3af",
                            fontWeight: 500,
                          }}
                        >
                          ID: {item.id}
                        </Typography>

                        <Stack direction="row" spacing={0.5}>
                          <Tooltip
                            title={
                              item.is_active
                                ? "Hide from site"
                                : "Show on site"
                            }
                            arrow
                          >
                            <IconButton
                              size="small"
                              onClick={() => handleToggleActive(item)}
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: "4px",
                                bgcolor: item.is_active
                                  ? alpha("#10b981", 0.1)
                                  : alpha("#ef4444", 0.1),
                                color: item.is_active ? "#10b981" : "#ef4444",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                  bgcolor: item.is_active
                                    ? alpha("#10b981", 0.2)
                                    : alpha("#ef4444", 0.2),
                                  transform: "scale(1.1)",
                                },
                              }}
                            >
                              {item.is_active ? (
                                <ViewIcon sx={{ fontSize: 15 }} />
                              ) : (
                                <HideIcon sx={{ fontSize: 15 }} />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(item.id)}
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: "4px",
                                bgcolor: alpha(brand, 0.1),
                                color: brand,
                                transition: "all 0.2s ease",
                                "&:hover": {
                                  bgcolor: alpha(brand, 0.2),
                                  transform: "scale(1.1)",
                                },
                              }}
                            >
                              <EditIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete" arrow>
                            <IconButton
                              size="small"
                              onClick={() =>
                                setDeleteDialog({
                                  open: true,
                                  testimonial: item,
                                })
                              }
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: "4px",
                                bgcolor: alpha("#ef4444", 0.1),
                                color: "#ef4444",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                  bgcolor: alpha("#ef4444", 0.2),
                                  transform: "scale(1.1)",
                                },
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      </Paper>

      {/* ── Delete Dialog ── */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, testimonial: null })}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "4px",
            border: "1px solid #e5e7eb",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            bgcolor: "#dc2626",
            px: 2.5,
            py: 1.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: "4px",
                bgcolor: "rgba(255,255,255,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <DeleteIcon sx={{ color: "#fff", fontSize: 16 }} />
            </Box>
            <Typography
              sx={{ fontWeight: 700, color: "#fff", fontSize: "0.9rem" }}
            >
              Delete Testimonial
            </Typography>
          </Stack>
          <IconButton
            onClick={() =>
              setDeleteDialog({ open: false, testimonial: null })
            }
            size="small"
            sx={{
              color: "#fff",
              borderRadius: "4px",
              "&:hover": { bgcolor: "rgba(255,255,255,0.15)" },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 2.5 }}>
          {deleteDialog.testimonial && (
            <Box sx={{ textAlign: "center", py: 1 }}>
              {/* Avatar */}
              {deleteDialog.testimonial.logo ? (
                <Avatar
                  src={deleteDialog.testimonial.logo}
                  sx={{
                    width: 56,
                    height: 56,
                    mx: "auto",
                    mb: 1.5,
                    border: `2px solid ${alpha(brand, 0.15)}`,
                  }}
                />
              ) : (
                <Avatar
                  sx={{
                    width: 56,
                    height: 56,
                    bgcolor: alpha(brand, 0.1),
                    color: brand,
                    fontSize: "1.1rem",
                    fontWeight: 800,
                    mx: "auto",
                    mb: 1.5,
                  }}
                >
                  {getInitials(deleteDialog.testimonial.name)}
                </Avatar>
              )}

              <Typography
                sx={{
                  fontWeight: 700,
                  color: "#111827",
                  fontSize: "0.9rem",
                  mb: 0.3,
                }}
              >
                {deleteDialog.testimonial.name}
              </Typography>
              {deleteDialog.testimonial.role && (
                <Typography
                  sx={{ fontSize: "0.72rem", color: "#6b7280", mb: 1 }}
                >
                  {deleteDialog.testimonial.role}
                </Typography>
              )}

              {/* Preview of the review */}
              <Box
                sx={{
                  bgcolor: "#f8fafc",
                  border: "1px solid #e5e7eb",
                  borderRadius: "4px",
                  p: 1.5,
                  mb: 1.5,
                  position: "relative",
                }}
              >
                <QuoteIcon
                  sx={{
                    position: "absolute",
                    top: 4,
                    left: 6,
                    fontSize: 20,
                    color: alpha(brand, 0.1),
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    color: "#374151",
                    fontStyle: "italic",
                    lineHeight: 1.5,
                    pl: 1,
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 3,
                    overflow: "hidden",
                  }}
                >
                  "{deleteDialog.testimonial.text}"
                </Typography>
              </Box>

              <Typography sx={{ fontSize: "0.78rem", color: "#6b7280" }}>
                Are you sure you want to delete this testimonial? This action
                cannot be undone.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            bgcolor: "#fef2f2",
            borderTop: "1px solid #fecaca",
            gap: 1,
          }}
        >
          <Button
            onClick={() =>
              setDeleteDialog({ open: false, testimonial: null })
            }
            size="small"
            sx={{
              borderRadius: "4px",
              textTransform: "none",
              fontSize: "0.75rem",
              px: 2,
              border: "1px solid #d1d5db",
              color: "#6b7280",
              boxShadow: "none",
              "&:hover": {
                borderColor: brand,
                color: brand,
                bgcolor: alpha(brand, 0.04),
                boxShadow: "none",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={deleting}
            variant="contained"
            size="small"
            startIcon={
              deleting ? (
                <CircularProgress size={14} sx={{ color: "#fff" }} />
              ) : (
                <DeleteIcon sx={{ fontSize: 15 }} />
              )
            }
            sx={{
              borderRadius: "4px",
              textTransform: "none",
              fontSize: "0.75rem",
              px: 2,
              bgcolor: "#dc2626",
              boxShadow: "none",
              "&:hover": { bgcolor: "#b91c1c", boxShadow: "none" },
              "&.Mui-disabled": {
                bgcolor: alpha("#dc2626", 0.5),
                color: "#fff",
              },
            }}
          >
            {deleting ? "Deleting..." : "Delete Testimonial"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{
    top: "80px !important",
  }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{
            borderRadius: "4px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TestimonialList;